import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  makeStyles,
  CircularProgress
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import TextField from "@material-ui/core/TextField";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: "100%",
  },
}));

const BulkTransferUserTicketsModal = ({ open, onClose, userId, userName, onSuccess }) => {
  const classes = useStyles();
  const [transferType, setTransferType] = useState("user");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const { findAll: findAllQueues } = useQueues();

  useEffect(() => {
    if (open) {
      loadQueues();
    }
  }, [open]);

  useEffect(() => {
    if (open && transferType === "user") {
      loadUsers();
    }
  }, [open, transferType]);

  const loadQueues = async () => {
    try {
      const list = await findAllQueues();
      setQueues(list);
    } catch (err) {
      toastError(err);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const { data } = await api.get("/users/");
      // Filtrar o próprio usuário
      setUsers(data.users.filter(u => u.id !== userId));
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleTransfer = async () => {
    if (transferType === "user" && !selectedUser) {
      toast.error("Selecione um usuário para transferir");
      return;
    }

    if (transferType === "queue" && !selectedQueue) {
      toast.error("Selecione uma fila para transferir");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        fromUserId: userId,
        transferType,
        ...(transferType === "user" ? { toUserId: selectedUser.id } : { queueId: selectedQueue })
      };

      const { data } = await api.post("/tickets/bulk-transfer-user", payload);
      
      toast.success(data.message || "Tickets transferidos com sucesso!");
      
      if (onSuccess) {
        onSuccess();
      }
      
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTransferType("user");
    setSelectedUser(null);
    setSelectedQueue("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Transferir Todos os Tickets de {userName}
      </DialogTitle>
      <DialogContent>
        <FormControl component="fieldset" className={classes.formControl}>
          <FormLabel component="legend">Tipo de Transferência</FormLabel>
          <RadioGroup
            value={transferType}
            onChange={(e) => setTransferType(e.target.value)}
          >
            <FormControlLabel
              value="user"
              control={<Radio color="primary" />}
              label="Para um usuário específico"
            />
            <FormControlLabel
              value="queue"
              control={<Radio color="primary" />}
              label="Para usuários de uma fila (distribuição proporcional)"
            />
          </RadioGroup>
        </FormControl>

        {transferType === "user" && (
          <FormControl className={classes.formControl}>
            <Autocomplete
              options={users}
              loading={loadingUsers}
              getOptionLabel={(option) => option.name}
              value={selectedUser}
              onChange={(e, newValue) => setSelectedUser(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Selecione o usuário"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </FormControl>
        )}

        {transferType === "queue" && (
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>Selecione a fila</InputLabel>
            <Select
              value={selectedQueue}
              onChange={(e) => setSelectedQueue(e.target.value)}
              label="Selecione a fila"
            >
              <MenuItem value="">
                <em>Nenhuma</em>
              </MenuItem>
              {queues.map((queue) => (
                <MenuItem key={queue.id} value={queue.id}>
                  {queue.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleTransfer}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Transferir Todos"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkTransferUserTicketsModal;
